import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQS = [
  {
    category: 'Sobre el servicio',
    color: '#135095',
    items: [
      {
        q: '¿Qué es Especialista en Heridas?',
        a: 'Especialista en Heridas es el principal directorio online de enfermeras especializadas en curación avanzada de heridas en Chile. Conectamos a pacientes y familias con profesionales de enfermería certificadas, disponibles para atención a domicilio en diversas regiones y comunas del país.'
      },
      {
        q: '¿Cuánto cuesta una curación de heridas a domicilio en Chile?',
        a: 'El costo varía según la complejidad de la herida (simple vs. avanzada), la distancia de desplazamiento, la región y la enfermera especialista. En nuestro directorio puedes ver el tipo de servicio de cada profesional y contactarla directamente para consultar sus tarifas actualizadas. Generalmente una curación simple puede partir desde $15.000 CLP, mientras que curaciones avanzadas (úlceras, pie diabético, terapia VAC) tienen un costo mayor.'
      },
      {
        q: '¿En qué regiones y comunas de Chile hay enfermeras especialistas disponibles?',
        a: 'Contamos con enfermeras en la Región Metropolitana (Santiago y comunas), Valparaíso, Biobío, La Araucanía, O\'Higgins, Maule, Los Lagos, Antofagasta y más. La cobertura crece constantemente. Puedes buscar por tu comuna en nuestro buscador para ver quién está disponible en tu zona.'
      },
      {
        q: '¿Cómo contacto a una enfermera especialista?',
        a: 'Desde el perfil de cada profesional puedes enviarle un mensaje interno a través de la plataforma o, si tiene el plan preferente, contactarla directamente por WhatsApp. Solo necesitas crear una cuenta gratuita de paciente para enviar mensajes.'
      },
      {
        q: '¿Las enfermeras del directorio están habilitadas para ejercer?',
        a: 'Sí. Todas las enfermeras registradas deben ser profesionales tituladas. Muchas cuentan con registro en la Superintendencia de Salud y certificaciones especializadas en curación de heridas, las que puedes verificar en su perfil junto con sus credenciales y diplomas.'
      }
    ]
  },
  {
    category: 'Tipos de heridas y tratamientos',
    color: '#2e7d32',
    items: [
      {
        q: '¿Qué tipo de heridas tratan las enfermeras especialistas?',
        a: 'Las enfermeras especialistas en heridas tratan una amplia variedad de lesiones: úlceras por presión (escaras), úlceras venosas y arteriales en extremidades inferiores, pie diabético, heridas postquirúrgicas de baja complejidad, heridas infectadas, ostomías, quemaduras superficiales y heridas crónicas de cualquier etiología. Las enfermeras generales también realizan curaciones simples, retiro de puntos, aplicación de inyecciones, instalación de vías venosas y manejo de sondas.'
      },
      {
        q: '¿Qué es una úlcera por presión (escara) y cómo se trata?',
        a: 'Una úlcera por presión es una lesión en la piel y los tejidos subyacentes causada por la presión prolongada sobre una zona del cuerpo, especialmente en pacientes encamados o con movilidad reducida. Se clasifican en 4 estadios según profundidad. El tratamiento incluye alivio de la presión (cambios de posición cada 2 horas), limpieza con suero fisiológico, aplicación de apósitos especializados (hidrocoloides, hidrogeles, alginatos) y, en casos avanzados, desbridamiento. Una enfermera especialista puede establecer un plan de curación personalizado y educación para cuidadores.'
      },
      {
        q: '¿Qué es el pie diabético y cómo se cuida la herida?',
        a: 'El pie diabético es una complicación grave de la diabetes mellitus que afecta los pies debido a neuropatía (pérdida de sensibilidad) y enfermedad vascular periférica. Puede producir úlceras que, sin tratamiento adecuado, pueden llevar a amputación. El manejo incluye: limpieza y desbridamiento de la herida, aplicación de apósitos apropiados según el tipo de tejido, control de la infección y alivio de la presión plantar. Es fundamental el seguimiento regular por una enfermera especialista, el control metabólico (glucemia) y la revisión vascular.'
      },
      {
        q: '¿Qué diferencia hay entre una úlcera venosa y una arterial?',
        a: 'Las úlceras venosas aparecen por insuficiencia venosa crónica, principalmente en la cara interna del tobillo. Suelen ser superficiales, con bordes irregulares, exudado abundante y edema. El tratamiento incluye compresión graduada y apósitos absorbentes. Las úlceras arteriales ocurren por isquemia (falta de flujo sanguíneo) y aparecen en zonas de presión o puntas de los dedos. Son profundas, con bordes definidos, poco exudado y extremidades frías. Requieren evaluación vascular urgente. Confundirlas puede ser peligroso: aplicar compresión en una úlcera arterial puede empeorarla gravemente.'
      },
      {
        q: '¿Qué es una ostomía y qué cuidados requiere a domicilio?',
        a: 'Una ostomía es una apertura quirúrgica en el abdomen que conecta el intestino o vejiga al exterior del cuerpo a través de un estoma (colostomía, ileostomía o urostomía). Requiere el uso de una bolsa colectora y cuidados regulares del estoma y la piel periestomal. Una enfermera especialista puede realizar el cambio de placa y bolsa, evaluar el estado del estoma, tratar complicaciones (dermatitis, hernias, prolapso) y educar al paciente y su familia en el autocuidado.'
      },
      {
        q: '¿Qué es la terapia VAC o terapia de presión negativa?',
        a: 'La terapia VAC (Vacuum Assisted Closure) o terapia de presión negativa (TPN) es un tratamiento avanzado para heridas complejas que utiliza presión subatmosférica controlada para estimular la cicatrización. Se coloca una esponja de poliuretano sobre la herida, se sella con un apósito adhesivo y se conecta a un dispositivo que genera vacío. Aumenta el flujo sanguíneo local, reduce el edema y el exudado, y estimula la formación de tejido de granulación. Es utilizado en úlceras complejas, heridas postquirúrgicas abiertas y heridas de alto exudado.'
      }
    ]
  },
  {
    category: 'Señales de alarma e infección',
    color: '#c62828',
    items: [
      {
        q: '¿Cuándo debo llamar urgentemente a una enfermera especialista?',
        a: 'Busca atención urgente si la herida presenta: enrojecimiento que se expande hacia la piel sana, calor y dolor intenso, pus o secreción de olor fétido, fiebre sobre 38°C, rayas rojas que se extienden desde la herida (linfangitis), herida que no mejora o empeora después de 2 semanas con curaciones regulares, o si el paciente tiene diabetes o inmunosupresión y presenta cualquier herida que no cierra.'
      },
      {
        q: '¿Cómo identifico si una herida tiene infección?',
        a: 'Los signos clásicos de infección en una herida son: dolor que aumenta en vez de disminuir, enrojecimiento (eritema) alrededor de la herida, calor local, hinchazón (edema), secreción purulenta (pus) o de mal olor, y fiebre sistémica. En heridas crónicas la infección puede presentarse de forma más sutil: aumento del exudado, cambio de color del tejido de granulación, mal olor o estancamiento de la cicatrización. Ante cualquier sospecha, consulta con una enfermera especialista.'
      },
      {
        q: '¿Qué NO debo poner en una herida?',
        a: 'Evita aplicar: alcohol o agua oxigenada (destruyen el tejido de cicatrización y retrasan la curación), yodo puro en heridas abiertas, pomadas antibióticas sin indicación médica, algodón directamente sobre la herida (deja fibras), cinta adhesiva directamente sobre piel frágil, y cualquier producto no estéril. La mayoría de las heridas se benefician simplemente de limpieza con suero fisiológico y un apósito apropiado.'
      },
      {
        q: '¿Cuándo se retiran los puntos de sutura?',
        a: 'El tiempo de retiro depende de la ubicación de la herida: cara (4-5 días), cuero cabelludo (7 días), tronco (7-10 días), extremidades superiores (7-10 días), extremidades inferiores (10-14 días), articulaciones (14 días). Sin embargo, cada caso es individual. Una enfermera puede evaluar si la herida ha cicatrizado adecuadamente antes de retirar los puntos y realizar el procedimiento de forma segura y aséptica a domicilio.'
      },
      {
        q: '¿Cuánto tiempo tarda en cicatrizar una herida crónica?',
        a: 'Una herida se considera crónica si no cicatriza en 4-6 semanas con tratamiento estándar. El tiempo de cicatrización varía enormemente según: tipo y profundidad de la herida, estado nutricional del paciente, enfermedades de base (diabetes, insuficiencia venosa), infección, tratamiento adecuado y constancia en los cuidados. Una úlcera por presión estadio 2 puede cicatrizar en semanas con buen manejo, mientras que una úlcera estadio 4 puede tardar meses. La evaluación regular por una enfermera especialista es clave para ajustar el tratamiento.'
      }
    ]
  },
  {
    category: 'Para enfermeras — Registrarse en el directorio',
    color: '#6a1b9a',
    items: [
      {
        q: '¿Cómo puedo registrarme como enfermera especialista en el directorio?',
        a: 'Puedes crear tu cuenta desde el botón "Registrarme" en la página principal. Una vez registrada, completa tu perfil con tu especialidad, región, comunas de cobertura, procedimientos que realizas, foto profesional y descripción de tu servicio. Tu perfil debe ser aprobado por el equipo de Especialista en Heridas antes de aparecer en el buscador.'
      },
      {
        q: '¿Qué diferencia hay entre el plan básico y el plan preferente?',
        a: 'Con el plan básico, los pacientes pueden contactarte únicamente a través de los mensajes internos de la plataforma. Con el plan preferente, los pacientes también pueden contactarte directamente por WhatsApp, lo que aumenta significativamente la tasa de contacto. Ambos planes te permiten publicar tu perfil completo con foto, procedimientos, credenciales y reseñas de pacientes.'
      },
      {
        q: '¿Cómo funcionan las reseñas de pacientes?',
        a: 'Los pacientes registrados pueden evaluar tu trabajo dejando una calificación de 1 a 5 estrellas y un comentario. Las reseñas con 4 o más estrellas se publican automáticamente en tu perfil. Las reseñas con menos de 4 estrellas son revisadas primero por el equipo de administración. Todas las reseñas aprobadas (automática o manualmente) aparecen en tu perfil público y contribuyen a tu calificación promedio.'
      }
    ]
  }
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.flatMap(cat =>
    cat.items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  )
};

export default function FAQ(props: any) {
  const { setLoading } = props;
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (setLoading) setLoading(false);

  return (
    <>
      <Helmet>
        <title>Preguntas Frecuentes — Curación de Heridas a Domicilio | Especialista en Heridas</title>
        <meta name="description" content="Resuelve tus dudas sobre curación de heridas, úlceras, pie diabético, terapia VAC, infecciones y más. Guía completa de enfermeras especializadas en Chile." />
        <link rel="canonical" href="https://especialistaenheridas.cl/preguntas-frecuentes" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #135095 0%, #1a6bc5 100%)',
        padding: '56px 24px',
        textAlign: 'center',
        color: '#fff'
      }}>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 12px', lineHeight: 1.2 }}>
          Preguntas Frecuentes
        </h1>
        <p style={{ fontSize: '17px', fontWeight: 300, margin: 0, opacity: 0.9, maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
          Todo lo que necesitas saber sobre curación de heridas, nuestro servicio y cómo encontrar a la profesional adecuada.
        </p>
      </div>

      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '40px 20px 60px' }}>

        {FAQS.map((section, si) => (
          <div key={si} style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{
                width: '4px', height: '28px', borderRadius: '2px',
                background: section.color, flexShrink: 0
              }} />
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                {section.category}
              </h2>
            </div>

            {section.items.map((item, ii) => {
              const key = `${si}-${ii}`;
              return (
                <Accordion
                  key={key}
                  expanded={expanded === key}
                  onChange={handleChange(key)}
                  elevation={0}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px !important',
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      borderColor: section.color,
                      boxShadow: `0 4px 20px ${section.color}22`
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: expanded === key ? section.color : '#111827',
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: expanded === key ? section.color : '#6b7280'
                      }
                    }}
                  >
                    {item.q}
                  </AccordionSummary>
                  <AccordionDetails sx={{ color: '#444', fontSize: '14px', lineHeight: 1.8, pt: 0 }}>
                    {item.a}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        ))}

        {/* CTA final */}
        <div style={{
          background: 'linear-gradient(135deg, #135095 0%, #1a6bc5 100%)',
          borderRadius: '16px',
          padding: '36px 32px',
          textAlign: 'center',
          color: '#fff',
          marginTop: '40px'
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: 700, color: '#fff' }}>
            ¿No encontraste tu respuesta?
          </h2>
          <p style={{ margin: '0 0 20px', opacity: 0.88, fontSize: '15px' }}>
            Busca una enfermera especialista en tu comuna y consúltale directamente.
          </p>
          <a
            href="/enfermeras"
            style={{
              display: 'inline-block',
              background: '#fff',
              color: '#135095',
              fontWeight: 700,
              padding: '12px 32px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '15px'
            }}
          >
            Buscar enfermera →
          </a>
        </div>
      </div>
    </>
  );
}
